
import { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../hooks/useAuth';
import { Link } from 'react-router-dom';
import StatsCard from './components/StatsCard';
import ChartSection from './components/ChartSection';
import DataTable from './components/DataTable';
import Header from './components/Header';
import AnnouncementsSection from '../employee-dashboard/components/AnnouncementsSection';
import PhotoInteractions from '../../components/feature/PhotoInteractions';

// Agent Link Card Component
function AgentLinkCard({ agent }: { agent: any }) {
  const handleAgentClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (!agent.hasValidLink) {
      alert(`No valid link available for ${agent.name}`);
      return;
    }

    let url = agent.link;

    console.log('Clicking agent:', agent.name);
    console.log('Agent link URL:', url);

    if (url && url.trim() !== '') {
      // Clean up the URL - it should already be extracted properly
      url = url.trim();

      // Ensure it has proper protocol
      if (!url.startsWith('http://') && !url.startsWith('https://')) {
        if (url.includes('docs.google.com') || url.includes('drive.google.com') || url.includes('sheets.google.com')) {
          url = 'https://' + url;
        }
      }

      console.log('Final URL to open:', url);

      // Validate URL format
      try {
        new URL(url);

        // Open in new tab
        const newWindow = window.open(url, '_blank', 'noopener,noreferrer');

        if (!newWindow) {
          console.log('Popup blocked, trying location.href');
          window.location.href = url;
        }
      } catch (urlError) {
        console.error('Invalid URL format:', url, urlError);
        alert(`Invalid URL format for ${agent.name}: ${url}`);
      }
    } else {
      console.log('No valid URL found for agent:', agent.name);
      alert('No valid link available for this agent.');
    }
  };

  return (
    <div
      onClick={handleAgentClick}
      className={`w-full p-4 rounded-xl border-2 transition-all duration-200 hover:shadow-lg hover:-translate-y-1 shadow-md ${
        agent.hasValidLink ? 'cursor-pointer' : 'cursor-not-allowed opacity-60'
      } text-left ${
        agent.isCompanyTotal
          ? 'bg-orange-50 border-orange-200 hover-bg-orange-100'
          : 'bg-blue-50 border-blue-200 hover-bg-blue-100'
      }`}
    >
      <div className="flex items-center space-x-3">
        <div className={`w-10 h-10 rounded-full flex items-center justify-center overflow-hidden shadow-sm ${
          agent.isCompanyTotal ? 'bg-orange-500' : 'bg-blue-500'
        }`}>
          {agent.photo ? (
            <img 
              src={agent.photo} 
              alt={agent.name}
              className="w-full h-full object-cover"
            />
          ) : (
            <i className={`${agent.isCompanyTotal ? 'ri-building-line' : 'ri-user-line'} text-white text-lg`}></i>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <h3 className={`font-semibold truncate ${
            agent.isCompanyTotal ? 'text-orange-700' : 'text-blue-700'
          }`}>
            {agent.name}
          </h3>
          <p className="text-xs text-gray-500 mt-1 flex items-center">
            {agent.hasValidLink ? (
              <>
                <i className="ri-external-link-line mr-1"></i>
                Click to visit link
              </>
            ) : (
              <>
                <i className="ri-close-circle-line mr-1"></i>
                No link available
              </>
            )}
          </p>
        </div>
        <i className="ri-arrow-right-s-line text-gray-400"></i>
      </div>
    </div>
  );
}

// Link Dropdown Component
function LinkDropdown({ section }: { section: any }) {
  const [isOpen, setIsOpen] = useState(false);

  const handleLinkClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (section.isUrl) {
      let url = section.link;

      if (url && url.trim() !== '') {
        url = url.trim().replace(/^[\"']|[\"']$/g, '');

        if (!url.startsWith('http://') && !url.startsWith('https://')) {
          url = 'https://' + url;
        }

        const newWindow = window.open(url, '_blank', 'noopener,noreferrer');

        if (!newWindow) {
          window.location.href = url;
        }
      }
    }
    setIsOpen(false);
  };

  const handleConditionalClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    // Handle conditional formatting value click - could be a link or other action
    if (section.conditionalValue) {
      console.log('Conditional formatting value clicked:', section.conditionalValue);

      // Check if the conditional value looks like a URL
      let conditionalUrl = section.conditionalValue;
      if (
        conditionalUrl.includes('http://') ||
        conditionalUrl.includes('https://') ||
        conditionalUrl.includes('docs.google.com') ||
        conditionalUrl.includes('drive.google.com')
      ) {
        if (!conditionalUrl.startsWith('http://') && !conditionalUrl.startsWith('https://')) {
          conditionalUrl = 'https://' + conditionalUrl;
        }

        const newWindow = window.open(conditionalUrl, '_blank', 'noopener,noreferrer');
        if (!newWindow) {
          window.location.href = conditionalUrl;
        }
      } else {
        // If it's not a URL, just show the value or perform other action
        alert(`Conditional formatting value: ${section.conditionalValue}`);
      }
    }
    setIsOpen(false);
  };

  // New: Handle E column option clicks
  const handleEColumnClick = (e: React.MouseEvent, option: any) => {
    e.preventDefault();
    e.stopPropagation();

    console.log('E column option clicked:', option);

    if (option.hasLink && option.link) {
      let url = option.link;

      if (!url.startsWith('http://') && !url.startsWith('https://')) {
        if (url.includes('docs.google.com') || url.includes('drive.google.com') || url.includes('sheets.google.com')) {
          url = 'https://' + url;
        }
      }

      const newWindow = window.open(url, '_blank', 'noopener,noreferrer');
      if (!newWindow) {
        window.location.href = url;
      }
    } else {
      // If no link, show the text value
      alert(`E column value: ${option.text}`);
    }

    setIsOpen(false);
  };

  // Calculate total available options
  const totalOptions =
    (section.eColumnOptions?.length || 0) +
    (section.hasConditionalFormatting && !section.useEColumn ? 1 : 0) +
    (section.hasValidLink ? 1 : 0);

  // Check if this is a payroll section for background styling
  const isPayrollSection = section.header.toLowerCase().includes('payroll');
  // Check if this is a reports section for background styling
  const isReportsSection = section.header.toLowerCase().includes('reports') || section.header.toLowerCase().includes('overview');

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`w-full bg-white border-2 border-gray-300 rounded-xl px-4 py-3 text-left shadow-md hover:bg-gray-50 hover:shadow-lg hover:-translate-y-0.5 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 ${
          isPayrollSection || isReportsSection ? 'relative overflow-hidden' : ''
        }`}
      >
        {/* Money symbol background for payroll sections */}
        {isPayrollSection && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <span className="text-3xl font-bold text-green-500 opacity-50 select-none">$</span>
          </div>
        )}
        
        {/* Bar graph icon background for reports sections */}
        {isReportsSection && !isPayrollSection && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <i className="ri-bar-chart-line text-3xl text-blue-500 opacity-50 select-none"></i>
          </div>
        )}
        
        <div className="flex items-center justify-between relative z-10">
          <div>
            <h3 className="text-sm font-medium text-gray-900">{section.header}</h3>
            <p className="text-xs text-gray-500 mt-1">
              {totalOptions > 0
                ? `${totalOptions} option${totalOptions > 1 ? 's' : ''} available`
                : 'No options available'}
            </p>
          </div>
          <i className={`ri-arrow-${isOpen ? 'up' : 'down'}-s-line text-gray-400 transition-transform`}></i>
        </div>
      </button>

      {isOpen && (
        <div className="absolute z-50 mt-1 w-full bg-white border-2 border-gray-300 rounded-xl shadow-xl max-h-64 overflow-y-auto backdrop-blur-sm">
          {/* Show E column options if available (for A18 dropdown) */}
          {section.useEColumn && section.eColumnOptions && section.eColumnOptions.length > 0 && (
            <>
              {section.eColumnOptions.map((option: any, index: number) => (
                <button
                  key={index}
                  onClick={(e) => handleEColumnClick(e, option)}
                  className="w-full px-4 py-3 text-left hover:bg-green-50 focus:outline-none focus:bg-green-50 transition-colors border-b border-gray-100 last:border-b-0 first:rounded-t-xl last:rounded-b-xl bg-white"
                >
                  <div className="flex items-center space-x-2">
                    <i className={`${option.hasLink ? 'ri-external-link-line text-green-600' : 'ri-file-text-line text-green-500'}`}></i>
                    <div className="flex-1">
                      <span className="text-sm text-green-700 hover:text-green-900 font-medium">
                        {option.text}
                      </span>
                    </div>
                    {option.hasLink && (
                      <i className="ri-arrow-right-up-line text-green-400 text-xs"></i>
                    )}
                  </div>
                </button>
              ))}
            </>
          )}

          {/* Show conditional formatting option if available (for other dropdowns) */}
          {!section.useEColumn && section.hasConditionalFormatting && section.conditionalValue && (
            <button
              onClick={handleConditionalClick}
              className="w-full px-4 py-3 text-left hover:bg-purple-50 focus:outline-none focus:bg-purple-50 transition-colors border-b border-gray-100 rounded-t-xl bg-white"
            >
              <div className="flex items-center space-x-2">
                <i className="ri-palette-line text-purple-500"></i>
                <div className="flex-1">
                  <span className="text-sm text-purple-600 hover:text-purple-800 font-medium">
                    Conditional Format
                  </span>
                  <p className="text-xs text-gray-500 truncate">
                    {section.conditionalValue}
                  </p>
                </div>
              </div>
            </button>
          )}

          {/* Show main link option if available */}
          {section.hasValidLink && section.link && (
            <button
              onClick={handleLinkClick}
              className="w-full px-4 py-3 text-left hover:bg-blue-50 focus:outline-none focus:bg-blue-50 transition-colors rounded-b-xl bg-white"
            >
              <div className="flex items-center space-x-2">
                <i className="ri-external-link-line text-blue-500"></i>
                <div className="flex-1">
                  <span className="text-sm text-blue-600 hover:text-blue-800 font-medium">
                    Main Link
                  </span>
                  <p className="text-xs text-gray-500 truncate">
                    {section.link}
                  </p>
                </div>
              </div>
            </button>
          )}

          {/* Show message if no options are available */}
          {totalOptions === 0 && (
            <div className="px-4 py-3 text-center rounded-xl bg-white">
              <i className="ri-information-line text-gray-400 text-lg mb-1"></i>
              <p className="text-xs text-gray-500">No options available</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Dashboard Component
export default function Dashboard() {
  const { user } = useAuth();

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [uploadedPhotos, setUploadedPhotos] = useState<Array<{
    id: string;
    url: string;
    name: string;
    uploadDate: string;
    type: string;
    uploadedBy?: string;
    storagePath?: string;
    folderId?: string;
    folderName?: string;
  }>>([]);
  const [mediaLoading, setMediaLoading] = useState(false);
  const [folders, setFolders] = useState<Array<{
    id: string;
    folder_name: string;
    description?: string;
  }>>([]);
  const [isAdmin, setIsAdmin] = useState(false);
  const [selectedFolderId, setSelectedFolderId] = useState<string>('');
  const [showFolderManager, setShowFolderManager] = useState(false);
  const [folderManagerMode, setFolderManagerMode] = useState<'create' | 'edit'>('create');
  const [editingFolder, setEditingFolder] = useState<{
    id: string;
    name: string;
    description?: string;
  } | null>(null);
  const [newFolderName, setNewFolderName] = useState('');
  const [newFolderDescription, setNewFolderDescription] = useState('');
  const [selectedFilterFolder, setSelectedFilterFolder] = useState<string>('all');
  const [stats, setStats] = useState({
    totalPolicies: 0,
    totalBrokerFee: 0,
    avgBrokerFee: 0,
    agentCount: 0
  });
  const [linkSections, setLinkSections] = useState([]);
  const [agentLinks, setAgentLinks] = useState([]);

  // Load folders on component mount
  useEffect(() => {
    const loadFolders = async () => {
      if (!user) return;
      
      try {
        const { data: folderData, error: folderError } = await supabase
          .from('media_folders')
          .select('*')
          .order('folder_name');

        if (folderError) throw folderError;

        setFolders(folderData || []);
        
        // Check if user is admin
        const { data: profileData } = await supabase
          .from('user_profiles')
          .select('role')
          .eq('user_id', user.id)
          .single();
        
        setIsAdmin(profileData?.role === 'admin');
        
        // Set default folder to General if it exists
        const generalFolder = folderData?.find(f => f.folder_name === 'General');
        if (generalFolder) {
          setSelectedFolderId(generalFolder.id);
        }
      } catch (error) {
        console.error('Error loading folders:', error);
      }
    };

    loadFolders();
  }, [user]);

  // Load media uploads on component mount
  useEffect(() => {
    const loadMediaUploads = async () => {
      if (!user) return;
      
      try {
        const { data: mediaData, error: mediaError } = await supabase
          .from('media_uploads')
          .select(`
            *,
            media_folders (
              folder_name
            )
          `)
          .order('uploaded_at', { ascending: false });

        if (mediaError) throw mediaError;

        const formattedMedia = (mediaData || []).map((media: any) => ({
          id: media.id,
          url: media.public_url,
          name: media.file_name,
          uploadDate: new Date(media.uploaded_at || Date.now()).toLocaleDateString(),
          type: media.file_type.startsWith('video/') ? 'video' : 'image',
          uploadedBy: media.uploaded_by,
          storagePath: media.storage_path,
          folderId: media.folder_id,
          folderName: media.media_folders?.folder_name || 'No Folder'
        }));
        setUploadedPhotos(formattedMedia);
      } catch (error) {
        console.error('Error loading media uploads:', error);
      }
    };

    loadMediaUploads();
  }, [user]);

  // Photo upload handler - Updated to use folder selection
  const handlePhotoUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || !user) return;

    setMediaLoading(true);
    const uploadPromises = Array.from(files).map(async (file) => {
      if (file.type.startsWith('image/') || file.type.startsWith('video/')) {
        try {
          // Upload to Supabase storage
          const fileExt = file.name.split('.').pop();
          const fileName = `${Math.random()}.${fileExt}`;
          const filePath = `${user.id}/${fileName}`;

          const { error: uploadError } = await supabase.storage
            .from('media')
            .upload(filePath, file);

          if (uploadError) throw uploadError;

          // Get public URL
          const { data: { publicUrl } } = supabase.storage
            .from('media')
            .getPublicUrl(filePath);

          // Save to database
          const { error: dbError } = await supabase
            .from('media_uploads')
            .insert({
              file_name: file.name,
              file_type: file.type,
              storage_path: filePath,
              public_url: publicUrl,
              uploaded_by: user.email || 'Unknown',
              folder_id: selectedFolderId || null
            });

          if (dbError) throw dbError;

          return true;
        } catch (error) {
          console.error(`Error uploading ${file.name}:`, error);
          return false;
        }
      }
      return false;
    });

    try {
      const results = await Promise.all(uploadPromises);
      const successCount = results.filter(Boolean).length;

      if (successCount > 0) {
        // Reload media uploads to show new files
        const { data: mediaData } = await supabase
          .from('media_uploads')
          .select(`
            *,
            media_folders (
              folder_name
            )
          `)
          .order('uploaded_at', { ascending: false });

        const formattedMedia = (mediaData || []).map((media: any) => ({
          id: media.id,
          url: media.public_url,
          name: media.file_name,
          uploadDate: new Date(media.uploaded_at || Date.now()).toLocaleDateString(),
          type: media.file_type.startsWith('video/') ? 'video' : 'image',
          uploadedBy: media.uploaded_by,
          storagePath: media.storage_path,
          folderId: media.folder_id,
          folderName: media.media_folders?.folder_name || 'No Folder'
        }));
        setUploadedPhotos(formattedMedia);

        if (successCount === files.length) {
          alert(`Successfully uploaded ${successCount} file${successCount > 1 ? 's' : ''}!`);
        } else {
          alert(`Uploaded ${successCount} out of ${files.length} files. Some uploads failed.`);
        }
      } else {
        alert('Failed to upload files. Please try again.');
      }
    } catch (error) {
      console.error('Error during upload process:', error);
      alert('An error occurred during upload. Please try again.');
    } finally {
      setMediaLoading(false);
      // Reset input
      event.target.value = '';
    }
  };

  // Delete Photo Handler
  const handleDeletePhoto = async (photoId: string, storagePath?: string) => {
    if (!storagePath) {
      console.error('No storage path provided for deletion');
      alert('Cannot delete: Missing storage path information');
      return;
    }

    // Show confirmation dialog
    const confirmDelete = window.confirm('Are you sure you want to permanently delete this media? This action cannot be undone and will remove all comments and reactions.');
    if (!confirmDelete) {
      return;
    }

    try {
      console.log('Starting comprehensive media deletion:', { photoId, storagePath });

      // Immediately remove from UI state to provide instant feedback
      setUploadedPhotos(prev => prev.filter(photo => photo.id !== photoId));

      // Delete from storage
      const { error: storageError } = await supabase.storage
        .from('media')
        .remove([storagePath]);

      if (storageError) throw storageError;

      // Delete from database (this will cascade delete reactions and comments)
      const { error: dbError } = await supabase
        .from('media_uploads')
        .delete()
        .eq('id', photoId);

      if (dbError) throw dbError;

      console.log('Media deletion completed successfully');
      alert('Media and all associated data deleted successfully!');
    } catch (error) {
      console.error('Error during deletion process:', error);
      
      // Restore the photo in UI if there was an error
      try {
        const { data: mediaData } = await supabase
          .from('media_uploads')
          .select(`
            *,
            media_folders (
              folder_name
            )
          `)
          .order('uploaded_at', { ascending: false });

        const formattedMedia = (mediaData || []).map((media: any) => ({
          id: media.id,
          url: media.public_url,
          name: media.file_name,
          uploadDate: new Date(media.uploaded_at).toLocaleDateString(),
          type: media.file_type.startsWith('video/') ? 'video' : 'image',
          uploadedBy: media.uploaded_by,
          storagePath: media.storage_path,
          folderId: media.folder_id,
          folderName: media.media_folders?.folder_name || 'No Folder'
        }));
        setUploadedPhotos(formattedMedia);
      } catch (restoreError) {
        console.error('Error restoring media state:', restoreError);
      }
      
      alert('An error occurred while deleting. Please try again.');
    }
  };

  // Folder management functions
  const handleCreateFolder = async () => {
    if (!newFolderName.trim()) {
      alert('Please enter a folder name');
      return;
    }

    try {
      const { error } = await supabase
        .from('media_folders')
        .insert({
          folder_name: newFolderName.trim(),
          description: newFolderDescription.trim() || null
        });

      if (error) throw error;

      // Reload folders
      const { data: folderData } = await supabase
        .from('media_folders')
        .select('*')
        .order('folder_name');

      setFolders(folderData || []);
      setNewFolderName('');
      setNewFolderDescription('');
      setShowFolderManager(false);
      alert('Folder created successfully!');
    } catch (error) {
      console.error('Error creating folder:', error);
      alert('Failed to create folder. Please try again.');
    }
  };

  const handleUpdateFolder = async () => {
    if (!editingFolder || !newFolderName.trim()) {
      alert('Please enter a folder name');
      return;
    }

    try {
      const { error } = await supabase
        .from('media_folders')
        .update({
          folder_name: newFolderName.trim(),
          description: newFolderDescription.trim() || null
        })
        .eq('id', editingFolder.id);

      if (error) throw error;

      // Reload folders
      const { data: folderData } = await supabase
        .from('media_folders')
        .select('*')
        .order('folder_name');

      setFolders(folderData || []);
      setNewFolderName('');
      setNewFolderDescription('');
      setEditingFolder(null);
      setShowFolderManager(false);
      alert('Folder updated successfully!');
    } catch (error) {
      console.error('Error updating folder:', error);
      alert('Failed to update folder. Please try again.');
    }
  };

  const handleDeleteFolder = async (folderId: string, folderName: string) => {
    if (!confirm(`Are you sure you want to delete the "${folderName}" folder? All media in this folder will be moved to the General folder.`)) {
      return;
    }

    try {
      const { error } = await supabase
        .from('media_folders')
        .delete()
        .eq('id', folderId);

      if (error) throw error;

      // Reload folders
      const { data: folderData } = await supabase
        .from('media_folders')
        .select('*')
        .order('folder_name');

      setFolders(folderData || []);
      
      // Reload media
      const { data: mediaData } = await supabase
        .from('media_uploads')
        .select(`
          *,
          media_folders (
            folder_name
          )
        `)
        .order('uploaded_at', { ascending: false });

      const formattedMedia = (mediaData || []).map((media: any) => ({
        id: media.id,
        url: media.public_url,
        name: media.file_name,
        uploadDate: new Date(media.uploaded_at || Date.now()).toLocaleDateString(),
        type: media.file_type.startsWith('video/') ? 'video' : 'image',
        uploadedBy: media.uploaded_by,
        storagePath: media.storage_path,
        folderId: media.folder_id,
        folderName: media.media_folders?.folder_name || 'No Folder'
      }));
      setUploadedPhotos(formattedMedia);
      
      if (selectedFolderId === folderId) {
        const generalFolder = folderData?.find(f => f.folder_name === 'General');
        setSelectedFolderId(generalFolder?.id || '');
      }
      
      alert('Folder deleted successfully!');
    } catch (error) {
      console.error('Error deleting folder:', error);
      alert('Failed to delete folder. Please try again.');
    }
  };

  const openFolderManager = (mode: 'create' | 'edit', folder?: {id: string; folder_name: string; description?: string}) => {
    setFolderManagerMode(mode);
    if (mode === 'edit' && folder) {
      setEditingFolder({ id: folder.id, name: folder.folder_name, description: folder.description });
      setNewFolderName(folder.folder_name);
      setNewFolderDescription(folder.description || '');
    } else {
      setEditingFolder(null);
      setNewFolderName('');
      setNewFolderDescription('');
    }
    setShowFolderManager(true);
  };

  // Filter photos by selected folder
  const filteredPhotos = selectedFilterFolder === 'all'
    ? uploadedPhotos
    : uploadedPhotos.filter(photo => photo.folderId === selectedFilterFolder);

  // Updated to use Supabase tables and fetch C14 value from Total row
  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setLoading(true);
        setError('');

        // Fetch MTD sales data to get all stats from the Total row
        const { data: mtdSalesData, error: mtdSalesError } = await supabase
          .from('mtd_sales_data')
          .select('*');

        if (mtdSalesError) {
          console.error('Error fetching MTD sales data:', mtdSalesError);
          throw new Error('Failed to fetch MTD sales data from database');
        }

        // Find the Total row which contains all the aggregated values
        const totalRow = mtdSalesData?.find(row => row.sales_agent === 'Total');

        let employeeData = [];
        let calculatedStats = {
          totalPolicies: 0,
          totalBrokerFee: 0,
          avgBrokerFee: 0,
          agentCount: 0
        };

        if (mtdSalesData && mtdSalesData.length > 0) {
          // Use the MTD sales data directly
          employeeData = mtdSalesData;
          
          // Calculate stats from the Total row
          const employees = mtdSalesData.filter(emp => emp.sales_agent !== 'Total');
          
          // Parse values from Total row
          const totalPolicies = totalRow?.policies_sold ? parseInt(totalRow.policies_sold.replace(/,/g, '')) : 0;
          const totalBrokerFeeTotal = totalRow?.broker_fee_total ? parseFloat(totalRow.broker_fee_total.replace(/[$,]/g, '')) : 0;
          const avgBrokerFeeAverage = totalRow?.broker_fee_average ? parseFloat(totalRow.broker_fee_average.replace(/[$,]/g, '')) : 0;
          
          calculatedStats = {
            totalPolicies: totalPolicies,
            totalBrokerFee: totalBrokerFeeTotal,
            avgBrokerFee: avgBrokerFeeAverage,
            agentCount: employees.length
          };

          console.log('Dashboard stats from mtd_sales_data Total row:', {
            totalPolicies,
            totalBrokerFeeTotal,
            avgBrokerFeeAverage,
            agentCount: employees.length,
            stats: calculatedStats
          });
        } else {
          console.log('No MTD sales data found in database, using mock data');
          
          // Fallback to mock data if no database data exists
          const mockData = [
            { id: 1, sales_agent: 'John Smith', broker_fee_average: '$120', policies_sold: '15', broker_fee_total: '$1800' },
            { id: 2, sales_agent: 'Sarah Johnson', broker_fee_average: '$135', policies_sold: '18', broker_fee_total: '$2430' },
            { id: 3, sales_agent: 'Mike Davis', broker_fee_average: '$110', policies_sold: '12', broker_fee_total: '$1320' },
            { id: 4, sales_agent: 'Lisa Wilson', broker_fee_average: '$125', policies_sold: '16', broker_fee_total: '$2000' },
            { id: 5, sales_agent: 'Total', broker_fee_average: '$122', policies_sold: '61', broker_fee_total: '$7550' }
          ];
          employeeData = mockData;
          const employees = mockData.filter(item => item.sales_agent !== 'Total');
          const mockTotalRow = mockData.find(item => item.sales_agent === 'Total');
          
          calculatedStats = {
            totalPolicies: parseInt(mockTotalRow?.policies_sold || '0'),
            totalBrokerFee: parseFloat(mockTotalRow?.broker_fee_total?.replace(/[$,]/g, '') || '0'),
            avgBrokerFee: parseFloat(mockTotalRow?.broker_fee_average?.replace(/[$,]/g, '') || '0'),
            agentCount: employees.length
          };
        }

        // Load agent links
        const predefinedAgentLinks = [
          {
            id: 1,
            name: 'AJ Overview',
            link: 'https://docs.google.com/spreadsheets/d/1UOq9yY2LbDOQjiraJTY-r_ulpKBirzT24QFe-BaAPJA/edit?gid=1827756569#gid=1827756569',
            hasValidLink: true,
            isCompanyTotal: false,
            photo: 'https://static.readdy.ai/image/179d4a3273ef4ee52c79cd3980b80e29/f2329cfbcf33ae9b0f2d8a91c6da9b69.png'
          },
          {
            id: 2,
            name: 'Adrian Overview',
            link: 'https://docs.google.com/spreadsheets/d/1UOq9yY2LbDOQjiraJTY-r_ulpKBirzT24QFe-BaAPJA/edit?gid=1985513524#gid=1985513524',
            hasValidLink: true,
            isCompanyTotal: false,
            photo: 'https://static.readdy.ai/image/179d4a3273ef4ee52c79cd3980b80e29/1cfddd0c323faee2e871d38fa787a874.png'
          },
          {
            id: 3,
            name: 'Alex Overview',
            link: 'https://docs.google.com/spreadsheets/d/1UOq9yY2LbDOQjiraJTY-r_ulpKBirzT24QFe-BaAPJA/edit?gid=1421693813#gid=1421693813',
            hasValidLink: true,
            isCompanyTotal: false,
            photo: 'https://static.readdy.ai/image/179d4a3273ef4ee52c79cd3980b80e29/9506a91e2b948a80a95df4f717459f04.png'
          },
          {
            id: 4,
            name: 'Fernando Overview',
            link: 'https://docs.google.com/spreadsheets/d/1UOq9yY2LbDOQjiraJTY-r_ulpKBirzT24QFe-BaAPJA/edit?gid=638748403#gid=638748403',
            hasValidLink: true,
            isCompanyTotal: false,
            photo: 'https://static.readdy.ai/image/179d4a3273ef4ee52c79cd3980b80e29/ca3a7c70ce6454b10b56db8c687bdb75.png'
          },
          {
            id: 5,
            name: 'Jeff Overview',
            link: 'https://docs.google.com/spreadsheets/d/1UOq9yY2LbDOQjiraJTY-r_ulpKBirzT24QFe-BaAPJA/edit?gid=588692189#gid=588692189',
            hasValidLink: true,
            isCompanyTotal: false,
            photo: 'https://static.readdy.ai/image/179d4a3273ef4ee52c79cd3980b80e29/4992c88e639f7fa3308eca513522c5b1.png'
          },
          {
            id: 6,
            name: 'Jenny Overview',
            link: 'https://docs.google.com/spreadsheets/d/1UOq9yY2LbDOQjiraJTY-r_ulpKBirzT24QFe-BaAPJA/edit?gid=1354788001#gid=1354788001',
            hasValidLink: true,
            isCompanyTotal: false,
            photo: 'https://static.readdy.ai/image/179d4a3273ef4ee52c79cd3980b80e29/38a681727cc72d08123d7abd82cb55f3.png'
          },
          {
            id: 7,
            name: 'Jesse Overview',
            link: 'https://docs.google.com/spreadsheets/d/1UOq9yY2LbDOQjiraJTY-r_ulpKBirzT24QFe-BaAPJA/edit?gid=982906933#gid=982906933',
            hasValidLink: true,
            isCompanyTotal: false,
            photo: 'https://static.readdy.ai/image/179d4a3273ef4ee52c79cd3980b80e29/7d761e0dd331f2381c412ba047eb3c0a.png'
          },
          {
            id: 8,
            name: 'Paul Overview',
            link: 'https://docs.google.com/spreadsheets/d/1UOq9yY2LbDOQjiraJTY-r_ulpKBirzT24QFe-BaAPJA/edit?gid=2115157659#gid=2115157659',
            hasValidLink: true,
            isCompanyTotal: false,
            photo: 'https://static.readdy.ai/image/179d4a3273ef4ee52c79cd3980b80e29/09fe48f131ff9b7d3d07da969b285d32.png'
          },
          {
            id: 9,
            name: 'Shan Overview',
            link: 'https://docs.google.com/spreadsheets/d/1UOq9yY2LbDOQjiraJTY-r_ulpKBirzT24QFe-BaAPJA/edit?gid=83734752#gid=83734752',
            hasValidLink: true,
            isCompanyTotal: false,
            photo: 'https://static.readdy.ai/image/179d4a3273ef4ee52c79cd3980b80e29/96dcddae3106e07139a385ae3570b561.png'
          },
          {
            id: 10,
            name: 'Steven Overview',
            link: 'https://docs.google.com/spreadsheets/d/1UOq9yY2LbDOQjiraJTY-r_ulpKBirzT24QFe-BaAPJA/edit?gid=2067270251#gid=2067270251',
            hasValidLink: true,
            isCompanyTotal: false,
            photo: 'https://static.readdy.ai/image/179d4a3273ef4ee52c79cd3980b80e29/958a53a03973a40f1fd7d402c4e31057.png'
          },
          {
            id: 11,
            name: 'Yesenia Overview',
            link: 'https://docs.google.com/spreadsheets/d/1UOq9yY2LbDOQjiraJTY-r_ulpKBirzT24QFe-BaAPJA/edit?gid=212174484#gid=212174484',
            hasValidLink: true,
            isCompanyTotal: false,
            photo: 'https://static.readdy.ai/image/179d4a3273ef4ee52c79cd3980b80e29/6952c9d71aed10b00d0b21770a6d2ee4.png'
          },
          {
            id: 12,
            name: 'Yulisa Overview',
            link: 'https://docs.google.com/spreadsheets/d/1UOq9yY2LbDOQjiraJTY-r_ulpKBirzT24QFe-BaAPJA/edit?gid=1816144414#gid=1816144414',
            hasValidLink: true,
            isCompanyTotal: false,
            photo: 'https://static.readdy.ai/image/179d4a3273ef4ee52c79cd3980b80e29/2bde41cba6dac6240f8e8c37ff03ce3a.png'
          },
          {
            id: 13,
            name: 'Quality Control Overview',
            link: 'https://docs.google.com/spreadsheets/d/1UOq9yY2LbDOQjiraJTY-r_ulpKBirzT24QFe-BaAPJA/edit?gid=788947469#gid=788947469',
            hasValidLink: true,
            isCompanyTotal: true,
            photo: 'https://static.readdy.ai/image/179d4a3273ef4ee52c79cd3980b80e29/9e5e529d8ef64368c6fe08a7dce47e7c.png'
          }
        ];

        console.log('Using predefined agent links:', predefinedAgentLinks);
        setAgentLinks(predefinedAgentLinks);

        // Process link sections - Get from original CSV for link sections
        const spreadsheetId = '1w0JwEZOkpeccRVQfI5Fco8KfZSjqmK8yULbyeVjz7aY';
        const csvUrl = `https://docs.google.com/spreadsheets/d/${spreadsheetId}/export?format=csv&gid=0`;

        try {
          const linkResponse = await fetch(csvUrl);
          if (linkResponse.ok) {
            const csvText = await linkResponse.text();
            const lines = csvText.split('\n').filter(line => line.trim());

            const linkSectionData = [];

            const linkDefinitions = [
              {
                headerRow: 16,
                conditionalRow: 17,
                linkRow: 18,
                defaultHeader: 'Owner Overview Reports',
                useEColumn: true,
                eColumnStartRow: 17,
                textColumnIndex: 4,
                linkColumnIndex: 5
              },
              {
                headerRow: 20,
                conditionalRow: 21,
                linkRow: 22,
                defaultHeader: 'Philippines Reports',
                useEColumn: true,
                eColumnStartRow: 17,
                textColumnIndex: 6,
                linkColumnIndex: 7
              },
              {
                headerRow: 23,
                conditionalRow: 24,
                linkRow: 25,
                defaultHeader: 'USA Payroll',
                useEColumn: true,
                eColumnStartRow: 17,
                textColumnIndex: 8,
                linkColumnIndex: 9
              },
              {
                headerRow: 27,
                conditionalRow: 28,
                linkRow: 29,
                defaultHeader: 'PH Payroll',
                useEColumn: true,
                eColumnStartRow: 17,
                textColumnIndex: 10,
                linkColumnIndex: 11
              }
            ];

            linkDefinitions.forEach((linkDef, sectionIndex) => {
              let headerValue = linkDef.defaultHeader;
              let conditionalValue = '';
              let linkValue = '';
              let hasValidLink = false;
              let hasConditionalFormatting = false;
              let eColumnOptions = [];

              if (linkDef.headerRow < lines.length) {
                const headerLine = lines[linkDef.headerRow];
                if (headerLine) {
                  const extractedHeader = headerLine.split(',')[0].replace(/"/g, '').trim();
                  if (extractedHeader && extractedHeader !== '' && extractedHeader !== '0') {
                    headerValue = extractedHeader;
                  }
                }
              }

              if (linkDef.useEColumn && linkDef.eColumnStartRow !== undefined) {
                const textColumnIndex = linkDef.textColumnIndex || 4;
                const linkColumnIndex = linkDef.linkColumnIndex || 5;

                for (let rowIndex = linkDef.eColumnStartRow; rowIndex < lines.length && rowIndex < linkDef.eColumnStartRow + 100; rowIndex++) {
                  const line = lines[rowIndex];
                  if (line) {
                    const values = [];
                    let currentValue = '';
                    let insideQuotes = false;

                    for (let i = 0; i < line.length; i++) {
                      const char = line[i];
                      if (char === '"') {
                        insideQuotes = !insideQuotes;
                      } else if (char === ',' && !insideQuotes) {
                        values.push(currentValue.trim());
                        currentValue = '';
                      } else {
                        currentValue += char;
                      }
                    }
                    values.push(currentValue.trim());

                    while (values.length < 50) {
                      values.push('');
                    }

                    const textColumnValue = values[textColumnIndex] ? values[textColumnIndex].replace(/"/g, '').trim() : '';
                    const linkColumnValue = values[linkColumnIndex] ? values[linkColumnIndex].replace(/"/g, '').trim() : '';

                    if (textColumnValue && textColumnValue !== '' && textColumnValue !== '0') {
                      let hyperlinkUrl = '';
                      if (linkColumnValue && linkColumnValue !== '' && linkColumnValue !== '0') {
                        if (linkColumnValue.includes('HYPERLINK') || linkColumnValue.includes('Hyperlink') || linkColumnValue.includes('hyperlink')) {
                          const hyperlinkPatterns = [
                            /=?HYPERLINK\s*\(\s*"([^"]+)"\s*,/i,
                            /=?Hyperlink\s*\(\s*"([^"]+)"\s*,/i,
                            /=?hyperlink\s*\(\s*"([^"]+)"\s*,/i,
                            /HYPERLINK\s*\(\s*"?([^",)]+)"?\s*,/i,
                            /(https:\/\/[^\s"',)]+)/i,
                            /(http:\/\/[^\s"',)]+)/i
                          ];

                          for (const pattern of hyperlinkPatterns) {
                            const match = linkColumnValue.match(pattern);
                            if (match && match[1]) {
                              hyperlinkUrl = match[1].trim().replace(/^["']|["']$/g, '');
                              break;
                            }
                          }
                        } else if (linkColumnValue.startsWith('http://') || linkColumnValue.startsWith('https://')) {
                          hyperlinkUrl = linkColumnValue;
                        } else if (linkColumnValue.includes('docs.google.com') || linkColumnValue.includes('drive.google.com')) {
                          hyperlinkUrl = linkColumnValue.startsWith('//') ? 'https:' + linkColumnValue : 'https://' + linkColumnValue;
                        }
                      }

                      const textColumnLetter = String.fromCharCode(65 + textColumnIndex);
                      const linkColumnLetter = String.fromCharCode(65 + linkColumnIndex);

                      eColumnOptions.push({
                        text: textColumnValue,
                        link: hyperlinkUrl,
                        hasLink: hyperlinkUrl !== '',
                        rowNumber: rowIndex + 1,
                        textColumn: textColumnLetter,
                        linkColumn: linkColumnLetter
                      });
                    }
                  }
                }

                hasConditionalFormatting = eColumnOptions.length > 0;
              }

              linkSectionData.push({
                id: sectionIndex + 1,
                header: headerValue,
                link: linkValue,
                conditionalValue,
                eColumnOptions,
                hasValidLink,
                hasConditionalFormatting,
                isUrl: hasValidLink,
                isEmpty: !linkValue || linkValue === '',
                isBlank: !headerValue || headerValue === linkDef.defaultHeader,
                useEColumn: linkDef.useEColumn || false
              });
            });

            setLinkSections(linkSectionData);
          }
        } catch (linkError) {
          console.error('Error fetching link sections:', linkError);
          setLinkSections([]);
        }

        // Set the employee data and stats from the database
        setData(employeeData);
        setStats(calculatedStats);

        console.log('Final employee data:', employeeData);
        console.log('Final stats from mtd_sales_data:', calculatedStats);

      } catch (err) {
        console.error('Error fetching dashboard data:', err);
        setError('Failed to load data from database. Using mock data.');
        // Mock data fallback...
        const mockData = [
          { id: 1, sales_agent: 'John Smith', broker_fee_average: '$120', policies_sold: '15', broker_fee_total: '$1800' },
          { id: 2, sales_agent: 'Sarah Johnson', broker_fee_average: '$135', policies_sold: '18', broker_fee_total: '$2430' },
          { id: 3, sales_agent: 'Mike Davis', broker_fee_average: '$110', policies_sold: '12', broker_fee_total: '$1320' },
          { id: 4, sales_agent: 'Lisa Wilson', broker_fee_average: '$125', policies_sold: '16', broker_fee_total: '$2000' },
          { id: 5, sales_agent: 'Total', broker_fee_average: '$122', policies_sold: '61', broker_fee_total: '$7550' }
        ];
        setData(mockData);
        const employeeData = mockData.filter(item => item.sales_agent !== 'Total');
        const totalRow = mockData.find(item => item.sales_agent === 'Total');
        setStats({
          totalPolicies: parseInt(totalRow?.policies_sold || '0'),
          totalBrokerFee: parseFloat(totalRow?.broker_fee_total?.replace(/[$,]/g, '') || '0'),
          avgBrokerFee: parseFloat(totalRow?.broker_fee_average?.replace(/[$,]/g, '') || '0'),
          agentCount: employeeData.length
        });
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  const handleRefresh = () => {
    window.location.reload();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading your dashboard data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-green-400">
      <Header onRefresh={handleRefresh} isConnected={!error} />

      <main className="w-full px-4 sm:px-6 lg:px-8 xl:px-12 2xl:px-16 py-8">
        {error && (
          <div className="mb-6 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div className="flex items-center">
              <i className="ri-warning-line text-yellow-600 mr-2"></i>
              <p className="text-yellow-800 text-sm">{error}</p>
            </div>
          </div>
        )}

        {/* Stats Cards */}
        <div className="mb-8">
          {/* Quick Actions */}
          <div className="mb-8">
            <div className="bg-white rounded-2xl shadow-sm p-5 max-w-4xl mx-auto">
              <div className="grid grid-cols-2 gap-3">
                <Link
                  to="/philippines-sales"
                  className="bg-purple-600 text-white rounded-xl p-4 text-sm font-medium transition-all shadow-sm hover:bg-purple-700 flex flex-col items-center justify-center"
                >
                  <i className="ri-bar-chart-line text-xl mb-2"></i>
                  <div>PH Sales MTD</div>
                </Link>

                <Link
                  to="/philippines-sales-today"
                  className="bg-purple-600 text-white rounded-xl p-4 text-sm font-medium transition-all shadow-sm hover:bg-purple-700 flex flex-col items-center justify-center"
                >
                  <i className="ri-calendar-line text-xl mb-2"></i>
                  <div>PH Sales Today</div>
                </Link>
                
                <Link
                  to="/mtd-sales"
                  className="bg-gradient-to-br from-purple-400 to-purple-500 text-white rounded-xl p-4 text-sm font-medium hover:from-purple-500 hover:to-purple-600 transition-all shadow-sm flex flex-col items-center justify-center"
                >
                  <i className="ri-calendar-check-line text-xl mb-2"></i>
                  <div>MTD Sales</div>
                </Link>
                
                <Link
                  to="/todays-sales"
                  className="bg-gradient-to-br from-orange-500 to-orange-600 text-white rounded-xl p-4 text-sm font-medium hover:from-orange-600 hover:to-orange-700 transition-all shadow-sm flex flex-col items-center justify-center"
                >
                  <i className="ri-calendar-todo-line text-xl mb-2"></i>
                  <div>Today's Sales</div>
                </Link>
                
                <Link
                  to="/retention-mtd"
                  className="bg-sky-600 text-white rounded-xl p-4 text-sm font-medium transition-all shadow-sm hover:bg-sky-700 flex flex-col items-center justify-center"
                >
                  <i className="ri-user-heart-line text-xl mb-2"></i>
                  <div>Retention MTD</div>
                </Link>

                {/* Modified block start */}
                <Link
                  to="/retention-today"
                  className="bg-sky-600 text-white rounded-xl p-4 text-sm font-medium transition-all shadow-sm hover:bg-sky-700 flex flex-col items-center justify-center"
                >
                  <i className="ri-user-heart-line text-xl mb-2"></i>
                  <div>Retention Today</div>
                </Link>

                <Link
                  to="/suggestions-admin"
                  className="bg-gradient-to-br from-indigo-600 to-indigo-700 text-white rounded-xl p-4 text-sm font-medium hover:from-indigo-700 hover:to-indigo-800 transition-all shadow-sm flex flex-col items-center justify-center"
                >
                  <i className="ri-lightbulb-line text-xl mb-2"></i>
                  <div>Suggestions</div>
                </Link>

                <Link
                  to="/tv-view"
                  className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-xl p-4 text-sm font-medium transition-all shadow-sm border-2 border-purple-300 hover:from-purple-700 hover:to-indigo-700 flex flex-col items-center justify-center"
                >
                  <i className="ri-tv-line text-xl mb-2"></i>
                  <div>TV View</div>
                </Link>

                <Link
                  to="/us-payroll"
                  className="bg-gradient-to-br from-green-600 to-green-700 text-white rounded-xl p-4 text-sm font-medium hover:from-green-700 hover:to-green-800 transition-all shadow-sm col-span-2 flex flex-col items-center justify-center"
                >
                  <i className="ri-money-dollar-circle-line text-xl mb-2"></i>
                  <div>US Payroll</div>
                </Link>

                <Link
                  to="/referrals"
                  className="bg-gradient-to-br from-teal-500 to-cyan-600 text-white rounded-xl p-4 text-sm font-medium hover:from-teal-600 hover:to-cyan-700 transition-all shadow-sm col-span-2 flex flex-col items-center justify-center"
                >
                  <i className="ri-user-add-line text-xl mb-2"></i>
                  <div>My Referrals</div>
                </Link>
                {/* Modified block end */}
              </div>
            </div>
          </div>

          {/* Updated Stats Card Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
            <StatsCard
              title="Total Policies Sold"
              value={stats.totalPolicies?.toString() || '0'}
              icon="ri-file-list-3-line"
              color="blue"
            />
            <StatsCard
              title="Total Broker Fee"
              value={`$${stats.totalBrokerFee?.toLocaleString() || '0'}`}
              icon="ri-money-dollar-circle-line"
              color="green"
            />
            <StatsCard
              title="Total Broker Fee Average"
              value={`$${Math.round(stats.avgBrokerFee || 0).toLocaleString()}`}
              icon="ri-bar-chart-line"
              color="purple"
            />
            <StatsCard
              title="Number of Agents"
              value={stats.agentCount?.toString() || '0'}
              icon="ri-team-line"
              color="indigo"
            />
          </div>
        </div>

        {/* Agent Report Cards Section */}
        {agentLinks.length > 0 && (
          <div className="mb-8">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <i className="ri-team-line text-blue-500 mr-2"></i>
              Agent Report Cards ({agentLinks.filter(agent => !agent.isCompanyTotal).length} agents found)
            </h2>
            <div className="bg-white rounded-xl shadow-lg border-2 border-gray-200 p-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6 gap-4">
                {agentLinks.map((agent) => (
                  <AgentLinkCard key={agent.id} agent={agent} />
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Quick Links Section */}
        <div className="mb-8">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <i className="ri-links-line text-blue-500 mr-2"></i>
            Quick Links ({linkSections.length} sections)
          </h2>
          <div className="grid grid-cols-1 sm-grid-cols-2 lg-grid-cols-4 gap-4">
            {linkSections.map((section) => (
              <LinkDropdown key={section.id} section={section} />
            ))}
          </div>
          {linkSections.length === 0 && (
            <div className="bg-gray-50 border-2 border-gray-200 rounded-xl shadow-md p-6 text-center">
              <i className="ri-links-line text-gray-400 text-2xl mb-1"></i>
              <p className="text-gray-500 text-sm">Quick Links will appear here when data is loaded</p>
            </div>
          )}
        </div>

        {/* Announcements Management Section */}
        <div className="mb-8">
          <AnnouncementsSection />
        </div>

        {/* Charts Section */}
        <div className="mb-8">
          <ChartSection data={data} />
        </div>

        {/* Data Table */}
        <div className="bg-white rounded-xl shadow-lg border-2 border-gray-200 overflow-hidden mb-8">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-medium text-gray-900">Driven Insurance Services Overall</h2>
            <p className="text-sm text-gray-600 mt-1">Live data as of {new Date().toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: '2-digit' })}</p>
          </div>
          <DataTable data={data} />
          
          {/* Photo Upload Section */}
          <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-md font-medium text-gray-900">Upload Media</h3>
              <div className="flex items-center space-x-4">
                {isAdmin && (
                  <button
                    onClick={() => openFolderManager('create')}
                    className="flex items-center px-3 py-1 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    <i className="ri-folder-add-line mr-1"></i>
                    Manage Folders
                  </button>
                )}
                <span className="text-sm text-gray-500">{uploadedPhotos.length} file{uploadedPhotos.length !== 1 ? 's' : ''} uploaded</span>
              </div>
            </div>

            {/* Folder Selection */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select Folder for Upload:
              </label>
              <select
                value={selectedFolderId}
                onChange={(e) => setSelectedFolderId(e.target.value)}
                className="w-full sm:w-64 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">No Folder</option>
                {folders.map((folder) => (
                  <option key={folder.id} value={folder.id}>
                    {folder.folder_name}
                    {folder.description && ` - ${folder.description}`}
                  </option>
                ))}
              </select>
            </div>
            
            <div className="flex flex-col sm:flex-row items-start sm:items-center space-y-2 sm:space-y-0 sm:space-x-4">
              <label className={`flex items-center px-4 py-2 rounded-lg transition-colors cursor-pointer ${
                mediaLoading 
                  ? 'bg-gray-400 text-white cursor-not-allowed' 
                  : 'bg-blue-600 text-white hover:opacity-80'
              }`}>
                <i className={`${mediaLoading ? 'ri-loader-4-line animate-spin' : 'ri-camera-line'} mr-2`}></i>
                {mediaLoading ? 'Uploading...' : 'Upload Photos & Videos'}
                <input
                  type="file"
                  multiple
                  accept="image/*,video/*"
                  onChange={handlePhotoUpload}
                  disabled={mediaLoading}
                  className="hidden"
                />
              </label>
              <p className="text-sm text-gray-500">
                {mediaLoading ? 'Please wait while files are being uploaded...' : 'Select photos and videos from your device'}
              </p>
            </div>
          </div>
        </div>

        {/* Photo Gallery Section */}
        {uploadedPhotos.length > 0 && (
          <div className="bg-white rounded-xl shadow-lg border-2 border-gray-200 overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-2 sm:space-y-0">
                <div>
                  <h2 className="text-lg font-semibold text-gray-900 flex items-center">
                    <i className="ri-gallery-line text-blue-500 mr-2"></i>
                    Driven Media Gallery
                  </h2>
                  <p className="text-sm text-gray-600 mt-1">
                    {filteredPhotos.length} of {uploadedPhotos.length} file{uploadedPhotos.length !== 1 ? 's' : ''} • Stored permanently in cloud
                  </p>
                </div>
                
                {/* Folder Filter */}
                <div className="flex items-center space-x-2">
                  <label className="text-sm font-medium text-gray-700">Filter by folder:</label>
                  <select
                    value={selectedFilterFolder}
                    onChange={(e) => setSelectedFilterFolder(e.target.value)}
                    className="px-3 py-1 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="all">All Folders</option>
                    {folders.map((folder) => (
                      <option key={folder.id} value={folder.id}>
                        {folder.folder_name}
                      </option>
                    ))}
                    <option value="">No Folder</option>
                  </select>
                </div>
              </div>
            </div>
            <div className="p-6 space-y-6">
              {filteredPhotos.map((photo) => (
                <div key={photo.id} className="border border-gray-200 rounded-lg overflow-hidden max-w-3xl mx-auto">
                  <div className="relative group">
                    <div className="w-full bg-gray-100 overflow-hidden">
                      {photo.type === 'video' ? (
                        <video
                          src={photo.url}
                          className="w-full h-auto object-contain"
                          controls
                          preload="metadata"
                        />
                      ) : (
                        <img
                          src={photo.url}
                          alt={photo.name}
                          className="w-full h-auto object-contain"
                        />
                      )}
                      {photo.type === 'video' && (
                        <div className="absolute top-2 left-2 bg-black bg-opacity-60 text-white px-2 py-1 rounded text-xs flex items-center">
                          <i className="ri-play-circle-line mr-1"></i>
                          Video
                        </div>
                      )}
                      <button
                        onClick={() => handleDeletePhoto(photo.id, photo.storagePath)}
                        className="absolute top-2 right-2 w-8 h-8 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600"
                        title="Delete media"
                      >
                        <i className="ri-close-line text-sm"></i>
                      </button>
                    </div>
                    
                    <div className="p-4 bg-gray-50 border-t border-gray-200">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium text-gray-900 flex items-center">
                            {photo.type === 'video' && <i className="ri-video-line mr-1 text-blue-500"></i>}
                            {photo.type === 'image' && <i className="ri-image-line mr-1 text-green-500"></i>}
                            {photo.name}
                          </p>
                          <p className="text-xs text-gray-500 mt-1 flex items-center">
                            <i className="ri-folder-line mr-1"></i>
                            <span className="font-medium">{photo.folderName}</span>
                            <span className="mx-2">•</span>
                            Uploaded by <span className="font-medium">{photo.uploadedBy}</span> on {photo.uploadDate}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Reactions and Comments */}
                  <PhotoInteractions mediaId={photo.id} />
                </div>
              ))}

              {filteredPhotos.length === 0 && (
                <div className="text-center py-8">
                  <i className="ri-folder-open-line text-4xl text-gray-400 mb-2"></i>
                  <p className="text-gray-500">No media files in this folder</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Folder Manager Modal */}
        {showFolderManager && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6 border-b border-gray-200 flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">
                  {folderManagerMode === 'create' ? 'Create New Folder' : 'Edit Folder'}
                </h3>
                <button
                  onClick={() => setShowFolderManager(false)}
                  className="w-8 h-8 bg-gray-100 hover:bg-gray-200 rounded-full flex items-center justify-center transition-colors"
                >
                  <i className="ri-close-line text-gray-600"></i>
                </button>
              </div>
              
              <div className="p-6">
                {/* Folder Form */}
                <div className="space-y-4 mb-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Folder Name *
                    </label>
                    <input
                      type="text"
                      value={newFolderName}
                      onChange={(e) => setNewFolderName(e.target.value)}
                      placeholder="Enter folder name"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Description (Optional)
                    </label>
                    <textarea
                      value={newFolderDescription}
                      onChange={(e) => setNewFolderDescription(e.target.value)}
                      placeholder="Enter folder description"
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  
                  <div className="flex space-x-3">
                    <button
                      onClick={folderManagerMode === 'create' ? handleCreateFolder : handleUpdateFolder}
                      className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      {folderManagerMode === 'create' ? 'Create Folder' : 'Update Folder'}
                    </button>
                    <button
                      onClick={() => setShowFolderManager(false)}
                      className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
                
                {/* Existing Folders List */}
                <div>
                  <h4 className="text-md font-medium text-gray-900 mb-3">Existing Folders</h4>
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {folders.map((folder) => (
                      <div key={folder.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div className="flex-1">
                          <p className="font-medium text-gray-900">{folder.folder_name}</p>
                          {folder.description && (
                            <p className="text-sm text-gray-500">{folder.description}</p>
                          )}
                        </div>
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => openFolderManager('edit', folder)}
                            className="w-8 h-8 bg-blue-100 hover:bg-blue-200 text-blue-600 rounded-full flex items-center justify-center transition-colors"
                            title="Edit folder"
                          >
                            <i className="ri-edit-line text-sm"></i>
                          </button>
                          {folder.folder_name !== 'General' && (
                            <button
                              onClick={() => handleDeleteFolder(folder.id, folder.folder_name)}
                              className="w-8 h-8 bg-red-100 hover:bg-red-200 text-red-600 rounded-full flex items-center justify-center transition-colors"
                              title="Delete folder"
                            >
                              <i className="ri-delete-bin-line text-sm"></i>
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </main>

      {/* Footer */}
      <footer className="bg-white border-t border-gray-200 py-4">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-sm text-gray-500">
            © 2013 Driven Insurance Services. All rights reserved. V2.0
          </p>
        </div>
      </footer>
    </div>
  );
}
